import { makeAutoObservable, runInAction } from "mobx";
import type { ApiUser } from "@hive/sdk";
import { getI18n, i18next } from "../i18n";
import { hive, requestSession } from "../lib/apiClient";
import { notifyAuthChanged } from "../lib/authChannel";
import { analyticsErrorCode, clearAnalyticsUser, setAnalyticsUser, track } from "../lib/analytics";
import { errorMessage } from "../lib/format";
import { clearPendingReferralCode, peekPendingReferralCode } from "../lib/referralCapture";

/**
 * Sign-in goes through the BFF's session routes rather than the SDK's auth resource: those
 * return the raw JWT in the body, which is precisely what must stop reaching this bundle. The
 * routes bank the token in an HttpOnly cookie and hand back only the user
 * (server/routes/auth-session.ts). Everything that does not mint a token — requesting an email
 * code, the whole non-auth API — still goes through the SDK.
 */
const SESSION_ROUTES = {
	session: "/auth/session",
	emailVerify: "/auth/session/email/verify",
	google: "/auth/session/google",
	apple: "/auth/session/apple",
	guest: "/auth/session/guest",
	signout: "/auth/session/signout",
	/** The server-side unified-logout redirect; also this store's sign-out fallback. */
	logout: "/logout",
} as const;

interface SessionResult {
	user: ApiUser;
	isNewUser?: boolean;
}

/**
 * Apply the user's saved UI locale, if any — profile preference wins over the
 * device's cached choice on sign-in.
 */
function applyUserLocale(user: ApiUser) {
	const locale = user.locale === "zh" || user.locale === "en" ? user.locale : null;
	if (locale && i18next.language !== locale) void i18next.changeLanguage(locale);
}

/**
 * Sign-in notice, naming the invite code when one rode along. Deliberately says "sent with your
 * signup" rather than "linked": the auth response carries no referral outcome, so the client
 * cannot tell an accepted code from one the server dropped (self-invite, already-registered user).
 * Tighten the copy once the API reports the result — MUHAHA-407.
 */
function signedInNotice(referralCode: string | null): string {
	return referralCode
		? i18next.t("auth.signedInWithInvite", { code: referralCode })
		: i18next.t("auth.signedIn");
}

export class AuthStore {
	user: ApiUser | null = null;
	/**
	 * 13+ compliance: the session cookie belongs to an account whose stored birthday is under the
	 * minimum age. Hive answers `ACCOUNT_UNDERAGE` to everything except reading and rewriting that
	 * birthday, so this is neither "signed in" (there is no usable user) nor "signed out" (there
	 * IS a session, and it is the only thing that can fix the account). The router sends it to
	 * /age-restriction; see `correctBirthday` for the way out.
	 */
	ageRestricted = false;
	initialized = false;
	loading = false;
	submitting = false;
	error = "";
	notice = "";

	constructor() {
		// displayName stays a plain getter (uncached) so its translated fallback
		// re-evaluates after a language switch.
		makeAutoObservable(this, { displayName: false }, { autoBind: true });
	}

	get isLoggedIn() {
		return Boolean(this.user);
	}

	get isGuest() {
		return this.user?.kind === "GUEST";
	}

	get displayName() {
		return this.user?.displayName || this.user?.handle || getI18n().t("auth.traveler");
	}

	clearFeedback() {
		this.error = "";
		this.notice = "";
	}

	setError(message: string) {
		this.error = message;
		this.notice = "";
	}

	setUser(user: ApiUser) {
		this.user = user;
		applyUserLocale(user);
		setAnalyticsUser(user);
	}

	/**
	 * Drops the session without a revoke call — for when it is already dead server-side, as
	 * after an account deletion. The cookie is HttpOnly, so only the server can clear it.
	 */
	async clearLocalSession() {
		try {
			await requestSession(SESSION_ROUTES.session, { method: "DELETE" });
		} catch {
			// Nothing left to do client-side; the cookie expires with the token either way.
		}
		clearAnalyticsUser(true);
		runInAction(() => {
			this.user = null;
			this.ageRestricted = false;
			this.clearFeedback();
		});
		notifyAuthChanged();
	}

	/**
	 * Resolves the session cookie into a user. Replaces the old localStorage peek + /auth/me
	 * pair: an HttpOnly cookie is invisible here, so the server answers both questions at once
	 * and reports `user: null` for "no session" — which is not an error and must not surface as
	 * one.
	 */
	async bootstrap() {
		if (this.initialized) return;
		this.loading = true;
		try {
			const { user, ageRestricted } = await requestSession<{
				user: ApiUser | null;
				ageRestricted?: boolean;
			}>(SESSION_ROUTES.session, { method: "GET" });
			runInAction(() => {
				this.ageRestricted = ageRestricted === true;
				if (user) this.setUser(user);
				else {
					clearAnalyticsUser(true);
					this.user = null;
				}
			});
		} catch (error) {
			clearAnalyticsUser(true);
			runInAction(() => {
				this.user = null;
				this.error = errorMessage(error, i18next.t("auth.sessionExpired"));
			});
		} finally {
			runInAction(() => {
				this.loading = false;
				this.initialized = true;
			});
		}
	}

	/**
	 * Re-asks the BFF who this browser is, ignoring `initialized`. Sign-in mints a token without
	 * consulting `/auth/me`, so a member whose stored birthday is under-age gets a "successful"
	 * login followed by 401s on everything — this is what turns that into the age notice instead.
	 */
	async refreshRestriction() {
		try {
			const { user, ageRestricted } = await requestSession<{
				user: ApiUser | null;
				ageRestricted?: boolean;
			}>(SESSION_ROUTES.session, { method: "GET" });
			runInAction(() => {
				this.ageRestricted = ageRestricted === true;
				if (this.ageRestricted) {
					clearAnalyticsUser(true);
					this.user = null;
				} else if (user) this.setUser(user);
			});
		} catch {
			// Leave the just-established session alone: a failed probe is not a verdict, and the
			// next API call surfaces the real state.
		}
	}

	/**
	 * The way out of `ageRestricted`: rewrite the account's birthday. `PATCH /profile/demographics`
	 * is the one write hive still accepts from an age-blocked member, and it refuses an under-age
	 * date — so this either unblocks the account or fails loudly. Gender is carried through because
	 * the endpoint replaces the whole demographics object.
	 */
	async correctBirthday(birthday: string): Promise<boolean> {
		this.submitting = true;
		this.clearFeedback();
		try {
			const current = await hive.profile.demographics();
			await hive.profile.updateDemographics({ birthday, gender: current.gender });
			runInAction(() => {
				this.ageRestricted = false;
			});
			// The account is no longer blocked, so /auth/me answers again — pull the identity this
			// session was never able to load.
			await this.refreshRestriction();
			notifyAuthChanged();
			return !this.ageRestricted;
		} catch (error) {
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("ageGate.saveFailed"));
			});
			return false;
		} finally {
			runInAction(() => {
				this.submitting = false;
			});
		}
	}

	async requestEmailCode(email: string): Promise<boolean> {
		this.submitting = true;
		this.clearFeedback();
		try {
			await hive.auth.requestEmailCode(email);
			track("auth_code_requested", { result: "success" });
			runInAction(() => {
				this.notice = i18next.t("auth.codeSent");
			});
			return true;
		} catch (error) {
			track("auth_code_requested", {
				result: "failure",
				error_code: analyticsErrorCode(error),
			});
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("auth.codeSendFailed"));
			});
			return false;
		} finally {
			runInAction(() => {
				this.submitting = false;
			});
		}
	}

	async verifyEmailCode(email: string, code: string): Promise<boolean> {
		this.submitting = true;
		this.clearFeedback();
		try {
			const referralCode = peekPendingReferralCode();
			const result = await requestSession<SessionResult>(SESSION_ROUTES.emailVerify, {
				body: { email, code, ...(referralCode ? { referralCode } : {}) },
			});
			runInAction(() => {
				this.setUser(result.user);
				this.notice = signedInNotice(referralCode);
			});
			notifyAuthChanged();
			clearPendingReferralCode();
			// Sign-in mints a token without consulting /auth/me, so an age-blocked account gets a
			// "successful" login here and 401s on everything after it. Ask once, now.
			await this.refreshRestriction();
			if (referralCode) track("referral_signup_attributed", { method: "email" });
			track("login_result", {
				method: "email",
				result: "success",
				user_kind: result.user.kind.toLowerCase(),
			});
			return true;
		} catch (error) {
			track("login_result", {
				method: "email",
				result: "failure",
				error_code: analyticsErrorCode(error),
			});
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("auth.codeInvalid"));
			});
			return false;
		} finally {
			runInAction(() => {
				this.submitting = false;
				this.initialized = true;
			});
		}
	}

	async signInWithGoogle(credential: string): Promise<boolean> {
		this.submitting = true;
		this.clearFeedback();
		try {
			const referralCode = peekPendingReferralCode();
			const result = await requestSession<SessionResult>(SESSION_ROUTES.google, {
				body: { idToken: credential, ...(referralCode ? { referralCode } : {}) },
			});
			runInAction(() => {
				this.setUser(result.user);
				this.notice = signedInNotice(referralCode);
			});
			notifyAuthChanged();
			clearPendingReferralCode();
			// Sign-in mints a token without consulting /auth/me, so an age-blocked account gets a
			// "successful" login here and 401s on everything after it. Ask once, now.
			await this.refreshRestriction();
			if (referralCode) track("referral_signup_attributed", { method: "google" });
			track("login_result", {
				method: "google",
				result: "success",
				user_kind: result.user.kind.toLowerCase(),
			});
			return true;
		} catch (error) {
			track("login_result", {
				method: "google",
				result: "failure",
				error_code: analyticsErrorCode(error),
			});
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("auth.googleFailed"));
			});
			return false;
		} finally {
			runInAction(() => {
				this.submitting = false;
				this.initialized = true;
			});
		}
	}

	async signInWithApple(input: {
		identityToken: string;
		authorizationCode: string;
		fullName?: { givenName?: string; familyName?: string };
	}): Promise<boolean> {
		this.submitting = true;
		this.clearFeedback();
		try {
			const referralCode = peekPendingReferralCode();
			const result = await requestSession<SessionResult>(SESSION_ROUTES.apple, {
				body: { ...input, ...(referralCode ? { referralCode } : {}) },
			});
			runInAction(() => {
				this.setUser(result.user);
				this.notice = signedInNotice(referralCode);
			});
			notifyAuthChanged();
			clearPendingReferralCode();
			// Sign-in mints a token without consulting /auth/me, so an age-blocked account gets a
			// "successful" login here and 401s on everything after it. Ask once, now.
			await this.refreshRestriction();
			if (referralCode) track("referral_signup_attributed", { method: "apple" });
			track("login_result", {
				method: "apple",
				result: "success",
				user_kind: result.user.kind.toLowerCase(),
			});
			return true;
		} catch (error) {
			track("login_result", {
				method: "apple",
				result: "failure",
				error_code: analyticsErrorCode(error),
			});
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("auth.appleFailed"));
			});
			return false;
		} finally {
			runInAction(() => {
				this.submitting = false;
				this.initialized = true;
			});
		}
	}

	async continueAsGuest(): Promise<boolean> {
		this.submitting = true;
		this.clearFeedback();
		try {
			const result = await requestSession<SessionResult>(SESSION_ROUTES.guest);
			runInAction(() => {
				this.setUser(result.user);
				this.notice = i18next.t("auth.guestEntered");
			});
			notifyAuthChanged();
			track("login_result", {
				method: "guest",
				result: "success",
				user_kind: result.user.kind.toLowerCase(),
			});
			return true;
		} catch (error) {
			track("login_result", {
				method: "guest",
				result: "failure",
				error_code: analyticsErrorCode(error),
			});
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("auth.guestFailed"));
			});
			return false;
		} finally {
			runInAction(() => {
				this.submitting = false;
				this.initialized = true;
			});
		}
	}

	async signOut() {
		this.submitting = true;
		try {
			// The route clears the session cookie whatever Hive answers, so the old "force-clear
			// the local token in the catch" dance is gone: a failed revoke can no longer leave
			// this browser holding a working credential.
			await requestSession(SESSION_ROUTES.signout);
			track("logout", { result: "success" });
		} catch (error) {
			track("logout", {
				result: "failure",
				error_code: analyticsErrorCode(error),
			});
			// The request never reached us (the route itself cannot fail), and only the server can
			// clear an HttpOnly cookie — so hand the browser to /logout, which does the same job
			// as a plain navigation. Callers navigate to "/" right after this anyway, and /logout
			// lands there; the alternative is a UI that says "signed out" over a live session.
			window.location.assign(SESSION_ROUTES.logout);
			throw error;
		} finally {
			clearAnalyticsUser(true);
			runInAction(() => {
				this.user = null;
				this.ageRestricted = false;
				this.clearFeedback();
				this.submitting = false;
			});
			notifyAuthChanged();
		}
	}
}

export const authStore = new AuthStore();
